import { useCallback, useEffect, useState } from "react";
import { Globe, Pencil, Send, Undo2, Plus, Trash2, ArrowUp, ArrowDown, ExternalLink } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { useContentRefresh } from "@/context/ContentContext";

function ListFieldEditor({ field, items, onChange }) {
  const { t } = useLang();
  const schema = field.item_schema || [];
  const update = (idx, name, value) => {
    const next = items.map((it, i) => (i === idx ? { ...it, [name]: value } : it));
    onChange(next);
  };
  const move = (idx, dir) => {
    const next = [...items];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    onChange(next);
  };
  return (
    <div className="space-y-3">
      {items.map((item, idx) => (
        <div key={idx} className="rounded-[12px] border border-border p-3 space-y-2 bg-muted/20" data-testid={`content-list-item-${idx}`}>
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">#{idx + 1}</span>
            <div className="flex gap-1">
              <Button type="button" variant="ghost" size="icon" className="h-7 w-7 rounded-[8px]" onClick={() => move(idx, -1)} data-testid={`content-list-up-${idx}`}><ArrowUp className="h-3.5 w-3.5" /></Button>
              <Button type="button" variant="ghost" size="icon" className="h-7 w-7 rounded-[8px]" onClick={() => move(idx, 1)} data-testid={`content-list-down-${idx}`}><ArrowDown className="h-3.5 w-3.5" /></Button>
              <Button type="button" variant="ghost" size="icon" className="h-7 w-7 rounded-[8px] text-destructive" onClick={() => onChange(items.filter((_, i) => i !== idx))} data-testid={`content-list-remove-${idx}`}><Trash2 className="h-3.5 w-3.5" /></Button>
            </div>
          </div>
          {schema.map((sf) => (
            <div key={sf.name} className="space-y-1">
              <Label className="text-xs">{sf.label}</Label>
              {sf.type === "textarea" ? (
                <Textarea rows={2} value={item[sf.name] || ""} onChange={(e) => update(idx, sf.name, e.target.value)} className="rounded-[10px] text-sm" data-testid={`content-list-field-${idx}-${sf.name}`} />
              ) : (
                <Input value={item[sf.name] || ""} onChange={(e) => update(idx, sf.name, e.target.value)} className="rounded-[10px] h-9 text-sm" data-testid={`content-list-field-${idx}-${sf.name}`} />
              )}
            </div>
          ))}
        </div>
      ))}
      <Button type="button" size="sm" variant="outline" className="rounded-[10px]" onClick={() => onChange([...items, Object.fromEntries(schema.map((sf) => [sf.name, ""]))])} data-testid="content-list-add">
        <Plus className="h-3.5 w-3.5 mr-1.5" /> {t("Eintrag hinzuf\u00fcgen", "Add entry")}
      </Button>
    </div>
  );
}

function LangFields({ section, lang, draft, setDraft }) {
  const fields = section.schema || [];
  const values = draft[lang] || {};
  const setField = (name, value) => setDraft({ ...draft, [lang]: { ...values, [name]: value } });
  return (
    <div className="space-y-4">
      {fields.map((f) => (
        <div key={f.name} className="space-y-1.5">
          <Label className="text-sm">{f.label}</Label>
          {f.type === "textarea" && (
            <Textarea rows={3} value={values[f.name] || ""} onChange={(e) => setField(f.name, e.target.value)} className="rounded-[10px]" data-testid={`content-field-${lang}-${f.name}`} />
          )}
          {f.type === "text" && (
            <Input value={values[f.name] || ""} onChange={(e) => setField(f.name, e.target.value)} className="rounded-[10px]" data-testid={`content-field-${lang}-${f.name}`} />
          )}
          {f.type === "list" && (
            <ListFieldEditor field={f} items={values[f.name] || []} onChange={(items) => setField(f.name, items)} />
          )}
        </div>
      ))}
    </div>
  );
}

function EditDialog({ section, onOpenChange, onSaved }) {
  const { t } = useLang();
  const refreshPublic = useContentRefresh();
  const [draft, setDraft] = useState({ de: {}, en: {} });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (section) setDraft({ de: { ...(section.draft?.de || {}) }, en: { ...(section.draft?.en || {}) } });
  }, [section]);

  if (!section) return null;

  const saveDraft = async (silent = false) => {
    const res = await api.put(`/cms/content/${section.key}`, draft);
    if (!silent) toast.success(t("Entwurf gespeichert", "Draft saved"));
    onSaved(res.data);
    return res.data;
  };

  const publish = async () => {
    try {
      setBusy(true);
      await saveDraft(true);
      const res = await api.post(`/cms/content/${section.key}/publish`);
      toast.success(t("Ver\u00f6ffentlicht \u2013 \u00c4nderungen sind live", "Published \u2013 changes are live"));
      onSaved(res.data);
      refreshPublic();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("Fehler beim Ver\u00f6ffentlichen", "Error publishing"));
    } finally {
      setBusy(false);
    }
  };

  const discard = async () => {
    try {
      setBusy(true);
      const res = await api.post(`/cms/content/${section.key}/discard`);
      toast.success(t("Entwurf verworfen", "Draft discarded"));
      onSaved(res.data);
      onOpenChange(false);
    } catch {
      toast.error(t("Fehler", "Error"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!section} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="content-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">{section.label}</DialogTitle>
          <DialogDescription>{section.description}</DialogDescription>
        </DialogHeader>
        <Tabs defaultValue="de">
          <TabsList className="rounded-[10px]">
            <TabsTrigger value="de" className="rounded-[8px]" data-testid="content-tab-de">Deutsch</TabsTrigger>
            <TabsTrigger value="en" className="rounded-[8px]" data-testid="content-tab-en">English</TabsTrigger>
          </TabsList>
          <TabsContent value="de" className="mt-4">
            <LangFields section={section} lang="de" draft={draft} setDraft={setDraft} />
          </TabsContent>
          <TabsContent value="en" className="mt-4">
            <LangFields section={section} lang="en" draft={draft} setDraft={setDraft} />
          </TabsContent>
        </Tabs>
        <DialogFooter className="gap-2 flex-wrap">
          <Button variant="ghost" className="rounded-[10px] text-muted-foreground" disabled={busy} onClick={discard} data-testid="content-discard">
            <Undo2 className="h-4 w-4 mr-1.5" /> {t("Entwurf verwerfen", "Discard draft")}
          </Button>
          <Button variant="outline" className="rounded-[10px]" disabled={busy} onClick={async () => { try { setBusy(true); await saveDraft(); } catch { toast.error(t("Fehler beim Speichern", "Error saving")); } finally { setBusy(false); } }} data-testid="content-save-draft">
            {t("Entwurf speichern", "Save draft")}
          </Button>
          <Button className="rounded-[10px] bg-[#0B1220]" disabled={busy} onClick={publish} data-testid="content-publish">
            <Send className="h-4 w-4 mr-1.5" /> {t("Ver\u00f6ffentlichen", "Publish")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminContent() {
  const { t } = useLang();
  const [sections, setSections] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    api.get("/cms/content").then((r) => setSections(r.data)).catch(() => setSections([]));
  }, []);
  useEffect(load, [load]);

  const onSaved = (updated) => {
    setSections((prev) => (prev || []).map((s) => (s.key === updated.key ? updated : s)));
    setEditing((prev) => (prev && prev.key === updated.key ? updated : prev));
  };

  return (
    <div className="space-y-6" data-testid="admin-content-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">{t("Website-Inhalte", "Website Content")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t("\u00d6ffentliche Texte ohne Code bearbeiten \u2013 Entwurf speichern und gezielt ver\u00f6ffentlichen", "Edit public texts without code \u2013 save drafts and publish deliberately")}</p>
        </div>
        <Button asChild variant="outline" className="rounded-[10px]" data-testid="content-view-site">
          <a href="/" target="_blank" rel="noreferrer"><ExternalLink className="h-4 w-4 mr-1.5" /> {t("Website ansehen", "View website")}</a>
        </Button>
      </div>

      {!sections ? (
        <div className="space-y-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-24 rounded-[16px]" />)}</div>
      ) : sections.length === 0 ? (
        <Card className="rounded-[16px]"><CardContent className="py-14 text-center text-sm text-muted-foreground">{t("Keine Inhalte gefunden.", "No content found.")}</CardContent></Card>
      ) : (
        <div className="space-y-3">
          {sections.map((s) => (
            <Card key={s.key} className="rounded-[14px]" data-testid={`content-section-${s.key}`}>
              <CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0 flex items-start gap-3">
                  <div className="h-9 w-9 rounded-[10px] bg-[#0B1220] flex items-center justify-center shrink-0 mt-0.5">
                    <Globe className="h-4 w-4 text-[#D6C08D]" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold">{s.label}</p>
                    <p className="text-xs text-muted-foreground">{s.description}</p>
                    <div className="mt-1.5 flex items-center gap-2">
                      <Badge variant="secondary" className="rounded-full text-[10px]">v{s.version}</Badge>
                      {s.has_draft_changes ? (
                        <Badge variant="outline" className="rounded-full text-[10px] border-amber-400/60 text-amber-700" data-testid={`content-draft-badge-${s.key}`}>{t("Entwurf ge\u00e4ndert", "Draft modified")}</Badge>
                      ) : (
                        <Badge variant="outline" className="rounded-full text-[10px] border-emerald-500/60 text-emerald-700">{t("Ver\u00f6ffentlicht", "Published")}</Badge>
                      )}
                      <span className="text-[11px] text-muted-foreground">{s.updated_by_name} {"\u00b7"} {new Date(s.updated_at).toLocaleString()}</span>
                    </div>
                  </div>
                </div>
                <Button size="sm" variant="outline" className="rounded-[10px] shrink-0" onClick={() => setEditing(s)} data-testid={`content-edit-${s.key}`}>
                  <Pencil className="h-3.5 w-3.5 mr-1.5" /> {t("Bearbeiten", "Edit")}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <EditDialog section={editing} onOpenChange={(o) => !o && setEditing(null)} onSaved={onSaved} />
    </div>
  );
}
